// ============================================================================
// EcgPatientHistoryDialog.tsx — ИСТОРИЯ ИССЛЕДОВАНИЙ ПАЦИЕНТА (экран врача).
//
// Открывается из очереди или из карточки исследования: показывает ВСЕ
// исследования выбранного пациента (запрос с отбором по пациенту/владельцу на
// сервере — не только отфильтрованную очередь), сводку и переход к любому
// исследованию. Пустые/ошибочные состояния явные, загрузка всегда завершается.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { CalendarClock, ChevronRight, FileText, RefreshCw, UserRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { formatValue } from '@/lib/format';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import { ECG_QUALITY_LABELS, ECG_STATUS_LABELS, listEcgStudies, type EcgStudyListItem } from '@/lib/ecgStudies';
import {
    patientDisplayName,
    summarizePatient,
    type EcgPatient,
    type EcgPatientSummary,
} from '@/lib/ecgPatients';
import { EcgStatusBadge } from '@/components/ecg/EcgStatusBadge';

interface Props {
    /** null — окно закрыто. */
    patient: EcgPatient | null;
    onClose: () => void;
    onOpenStudy: (studyId: string) => void;
}

function createdAtLabel(study: EcgStudyListItem): string {
    return formatValue(study.createdAt, 'date:dd.MM.yyyy HH:mm', 'timestamp') || study.title || '—';
}

function hrLabel(study: EcgStudyListItem): string {
    const hr = study.measurements.hrBpm;
    return hr === null ? '—' : String(Math.round(hr));
}

export function EcgPatientHistoryDialog({ patient, onClose, onOpenStudy }: Props) {
    const [summary, setSummary] = useState<EcgPatientSummary | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [reloadKey, setReloadKey] = useState(0);

    const load = useCallback(async () => {
        if (!patient) {
            setSummary(null);
            setError('');
            setLoading(false);
            return;
        }
        setLoading(true);
        setError('');
        try {
            const studies = await listEcgStudies({ patient });
            setSummary(summarizePatient(studies, patient));
        } catch (e) {
            setSummary(null);
            setError(friendlyError(e));
            logErrorToJournal(e, 'ЭКГ: история исследований пациента');
        } finally {
            setLoading(false);
        }
    }, [patient]);

    useEffect(() => {
        void load();
    }, [load, reloadKey]);

    const name = patientDisplayName(patient);
    const studies = summary?.studies || [];
    const lastLabel = summary?.lastAt
        ? formatValue(summary.lastAt, 'date:dd.MM.yyyy HH:mm', 'timestamp')
        : '';

    return (
        <UniversalWindow
            open={!!patient}
            onClose={onClose}
            title={patient ? `История пациента: ${name}` : 'История пациента'}
            width="w-[min(900px,95vw)]"
            height="h-[min(80vh,90vh)]"
        >
            <div className="h-full overflow-auto p-4 space-y-3">
                <section className="border rounded-lg bg-card">
                    <div className="flex items-center gap-2 px-3 py-2 border-b flex-wrap">
                        <UserRound className="h-4 w-4 text-primary shrink-0" />
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium truncate">{name}</div>
                            <div className="text-[11px] text-muted-foreground">
                                {patient?.source === 'patient'
                                    ? 'Пациент из картотеки'
                                    : 'Владелец персональных записей ЭКГ'}
                                {summary ? ` · исследований: ${summary.total}` : ''}
                                {lastLabel ? ` · последнее: ${lastLabel}` : ''}
                            </div>
                        </div>
                        <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-[11px]"
                            onClick={() => void load()}
                            disabled={loading || !patient}
                        >
                            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
                            Обновить
                        </Button>
                    </div>

                    {summary && (
                        <div className="px-3 py-2 grid grid-cols-2 md:grid-cols-3 gap-2 text-[11px] text-muted-foreground">
                            <div>
                                <div>С заключением</div>
                                <div className="text-sm text-foreground">{summary.withConclusion}</div>
                            </div>
                            <div>
                                <div>Ожидают заключения</div>
                                <div className="text-sm text-foreground">{summary.awaitingConclusion}</div>
                            </div>
                            <div>
                                <div>Статусы</div>
                                <div className="text-sm text-foreground flex flex-wrap gap-1 items-center">
                                    {studies.length === 0
                                        ? '—'
                                        : Object.entries(summary.statusCounts).map(([code, count]) => (
                                            <span key={code} className="inline-flex items-center gap-1">
                                                <EcgStatusBadge code={code} label={ECG_STATUS_LABELS[code]} />
                                                <span className="text-muted-foreground">×{count}</span>
                                            </span>
                                        ))}
                                </div>
                            </div>
                        </div>
                    )}
                </section>

                {loading && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        Загружаю историю исследований…
                    </div>
                )}

                {!loading && error && (
                    <div className="text-sm text-destructive border border-destructive/40 rounded-md px-3 py-2 flex items-center gap-3">
                        <span className="flex-1">{error}</span>
                        <Button size="sm" variant="outline" className="h-7" onClick={() => setReloadKey((value) => value + 1)}>
                            Повторить
                        </Button>
                    </div>
                )}

                {!loading && !error && summary && studies.length === 0 && (
                    <div className="text-sm text-muted-foreground border rounded-lg bg-card px-3 py-4 flex items-start gap-2">
                        <CalendarClock className="h-4 w-4 mt-0.5 shrink-0" />
                        <span>
                            У пациента пока нет исследований. Сделайте разбор во вкладке «Новый разбор» —
                            исследование появится в истории.
                        </span>
                    </div>
                )}

                {!error && studies.length > 0 && (
                    <div className="border rounded-lg bg-card overflow-hidden">
                        <table className="w-full text-sm border-collapse">
                            <thead>
                                <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                                    <th className="px-2 py-1.5 font-medium">Дата</th>
                                    <th className="px-2 py-1.5 font-medium">Исследование</th>
                                    <th className="px-2 py-1.5 font-medium">Статус</th>
                                    <th className="px-2 py-1.5 font-medium">ЧСС</th>
                                    <th className="px-2 py-1.5 font-medium">Качество</th>
                                    <th className="px-2 py-1.5 font-medium">Заключение</th>
                                    <th className="px-2 py-1.5 font-medium text-right"> </th>
                                </tr>
                            </thead>
                            <tbody>
                                {studies.map((study) => {
                                    const hasConclusion = !!study.conclusion.trim();
                                    const open = () => onOpenStudy(study.id);
                                    return (
                                        <tr
                                            key={study.id}
                                            className="border-t hover:bg-accent/30 cursor-pointer focus:outline-none focus-visible:ring-1 focus-visible:ring-primary"
                                            tabIndex={0}
                                            onClick={open}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                    event.preventDefault();
                                                    open();
                                                }
                                            }}
                                            aria-label={`Открыть исследование ${createdAtLabel(study)}`}
                                        >
                                            <td className="px-2 py-1.5 whitespace-nowrap">{createdAtLabel(study)}</td>
                                            <td className="px-2 py-1.5 max-w-[240px] truncate" title={study.title}>
                                                {study.title || '—'}
                                            </td>
                                            <td className="px-2 py-1.5">
                                                <EcgStatusBadge code={study.statusCode} label={study.statusLabel} />
                                            </td>
                                            <td className="px-2 py-1.5 whitespace-nowrap">{hrLabel(study)}</td>
                                            <td className="px-2 py-1.5 whitespace-nowrap">
                                                {ECG_QUALITY_LABELS[study.qualityVerdict]}
                                            </td>
                                            <td className="px-2 py-1.5">
                                                <span className={`inline-flex items-center gap-1 text-[11px] ${hasConclusion ? 'text-emerald-700' : 'text-amber-700'}`}>
                                                    <FileText className="h-3.5 w-3.5" />
                                                    {hasConclusion ? 'есть' : 'ожидает'}
                                                </span>
                                            </td>
                                            <td className="px-2 py-1.5 text-right">
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="h-6 px-2 text-[11px]"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        open();
                                                    }}
                                                >
                                                    Открыть
                                                    <ChevronRight className="h-3.5 w-3.5" />
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </UniversalWindow>
    );
}
